import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import FormContext from "../../../context/form-context";
import { isError, setAlert, setFormErrors, setFormState, setFormTypeField } from '../../../state/action';
import { ACTION } from "../../../constants/form-action";
import { defaultForm , defaultType } from "../../../constants/form-type";
import {  addCommasOnNumber, evaluateFormula, getDetailedDates_Individuals, getDetailedDates_Ordinal, numberToOrdinalDay, sortByFields, sortBySections } from "../../../utils/utilities";
import { useLocation, useParams } from "react-router-dom";
import { commissionMapping, commissionPersonsMapping, fieldDefaultValuesMapping, propertyAddressByPostalMapping, propertyAddressMapping, salesPersonMapping } from "../fields-mapping";
import { agent, agentName, amountForSales, associateName, billToField, buyer, cobroke, commencementDateField, expiryDateField, gst, landlord, monetaryKeywords, monthlyRental, nettCommission, optionDate, paymentMode, paymentModeField, salesPerson, submissionDate, tenant, termsOfLeaseMonthsField } from "../specialFields";
import { defaultFormState } from "../../../utils/initialFormState";
import { FIELDTYPE } from "../../../constants/field-type";
import { fetchPropertyByPostalCode } from "../../../services/real-star-api/property/property";
import { fetchGSTRate } from "../../../services/real-star-api/property/gst";
import dayjs from "dayjs";
import { fetchPaymentModeOpt } from "../../../services/real-star-api/property/payment-mode";
import {  fetchNationalityOpt } from "../../../services/real-star-api/nationality/nationality";
import { validateFile } from "../../ViewPDFPage/pdfUtil";

const useMainForm = () => {
    const { appState, dispatch } = useContext(FormContext);
    const { agentDetails , error , agentNames} = appState;
    const { fields, form, sections, type } = appState.formType;
    const { action } = appState.formInfo;
    const [mainFields, setMainFields] = useState(fields)
    const [mainSections, setMainSections] = useState(sections)
    const mainFormState = appState.formState.mainValues
    const mainFormErrors = appState.formErrors.mainValues;
    const location = useLocation();
    const routeProperty = location.state?.propertyDetails
    const routePropertyID = location.state?.propertyID
    const routeForm = location.state?.form
    const routeType = location.state?.type
    const [isMounted , setIsMounted] = useState(false)
    const [pageError , setPageError] = useState(false)
    const [paymentModeOptions , setPaymentModeOptions] = useState([])
    const [nationalityOptions , setNationalityOptions] = useState([])
    const { postalCode } = useParams();

    useEffect(() => {
        const initializeFormState = async () => {
            
            if (action === ACTION.CREATE) {
                
                const defaultState = await defaultFormState(appState.formType );
                //addtional State if BDC - Sales
                const gstRate = await fetchGSTRate();
                const gST = gstRate.toString(); //GST Default Value from API
                const commission = await commissionMapping(agentDetails, form , defaultForm)
                const salesPerson = await salesPersonMapping(agentDetails)
                const withDefaultValues = await fieldDefaultValuesMapping()

                //if from routes && must equal to BDC - Sales
                if (
                    routeProperty //routeProperty has value, or if the request comes from Route
                    &&
                    (defaultForm[1] === routeForm)  //if form (e.g BDC === BDC)
                ) {
                    if (routeForm && routeType) {
                        dispatch(setFormTypeField({
                            ...appState.formType,
                            form: routeForm,
                            type: routeType
                        }))
                    }

                    const propertyFields = await propertyAddressMapping(routeProperty , routePropertyID); //Mapping
                    const commissionPersons = await commissionPersonsMapping(routeProperty, agentNames); //Mapping

                    dispatch(setFormState({
                        ...appState.formState,
                        mainValues: {
                            ...defaultState.mainValues,
                            gST, //default value of GST
                            ...propertyFields,
                            ...commission,
                            ...salesPerson,
                            ...withDefaultValues,
                            ...commissionPersons
                        }
                    }));
                } 
                else if (postalCode){
                    try {
                        const propertyDetails = await fetchPropertyByPostalCode(postalCode);
                        const property = await propertyAddressByPostalMapping(propertyDetails)
                        dispatch(setFormState({
                            ...appState.formState,
                            mainValues: {
                                ...defaultState.mainValues,
                                gST, //default value of GST
                                ...property,
                                ...((form === defaultForm[1] && type === defaultType[0]) && commission), //if BDC-SALES
                                ...((form === defaultForm[1] && type === defaultType[0]) && salesPerson), //if BDC-SALES
                                ...withDefaultValues
                                
                            }
                        }));
                    } catch (error) {
                        dispatch(setAlert({
                            show: true,
                            title: `Error: Postal Code`,
                            body: `${postalCode} does not exist`
                        }));
                    }
                }else {
                    //default form state
                    dispatch(setFormState({
                        ...appState.formState,
                        mainValues: {
                            ...defaultState.mainValues,
                            gST, //default value of GST
                            ...((form === defaultForm[1] && type === defaultType[0]) && commission), //if BDC-SALES
                            ...((form === defaultForm[1] && type === defaultType[0]) && salesPerson), //if BDC-SALES
                            ...withDefaultValues
                        }
                    }));
                }
                dispatch(setFormErrors(defaultState))
                
            }
            setIsMounted(true)
        }

        const initPaymentModeOptions = async () => {
            const paymentModeOpt = await fetchPaymentModeOpt();
            const newOpt = paymentModeOpt.map( i => {
                return {payment_mode_id: i.f_id , payment_mode: i.f_payment_mode , key: i.f_id}
            } )
            setPaymentModeOptions(newOpt)
        } 

        const initNationalityOptions = async () => {
            const nationalityOpt = await fetchNationalityOpt();
            const newOpt = nationalityOpt && nationalityOpt.map( i =>  `${i.country_name} (${i.nationality})` )
            const uniqueNationalities = [...new Set(newOpt)]
            setNationalityOptions(uniqueNationalities)

        } 

        initializeFormState();
        initPaymentModeOptions();
        initNationalityOptions();
       
    }, [])

    useEffect(() => {

            const newFields = fields?.map((field) => {
                // special handling for Sales Person fields.
                // options: All Agents for Sales person and Agent name
                if (
                    (   
                        field.FieldName2.toLowerCase().includes(salesPerson.toLowerCase()) ||
                        field.FieldName2.toLowerCase().includes(agentName.toLowerCase()) || 
                        (
                            field.FieldName2.toLowerCase().includes(associateName.toLowerCase()) &&
                            field.FieldName2.toLowerCase().includes("name")
                        )
                    ) 
                    && field.FieldType === FIELDTYPE.DROP_DOWN) 
                {
                    field.FieldOptions = agentNames.map( i => i.agentName);

                } 
                if(field.FieldName2.toLowerCase().includes("nationality") && field.FieldType === FIELDTYPE.DROP_DOWN){
                    field.FieldOptions = nationalityOptions;
                }

                return {
                    ...field,
                    Disabled: field.Disabled || action === ACTION.VIEW //disabled if edit.
                }
            })
            setMainFields(sortByFields(newFields))
            setMainSections(sortBySections(sections))
           
    }, [action, nationalityOptions])

    useEffect( () => {
        //on Error
        const hideAlert = async () => {
            const timer = await setTimeout(() => {
              setPageError(prevError => ({ ...prevError, show: false }));
              dispatch(isError({show: false, errorMessage:''}))
            }, 4000);

            return () => clearTimeout(timer);
        }

        if(error.show && error.errorMessage){
            setPageError(error)
            hideAlert();
        }

        return () => {
            setPageError(false)
        }
    }, [error])


    const handleMainFieldChange = useCallback((val, field) => {
        const { mainValues } = appState.formState;

        // if the field/s are for Price, amount ,fee , money
        const isPriceOrAmountField = monetaryKeywords.some(keyword =>
            field?.toLowerCase().includes(keyword)
        );
        
        const isCommissionerField = field?.toLowerCase().includes(salesPerson.toLowerCase()) || //salesperson
            field?.toLowerCase().includes(agentName.toLowerCase()) || //with agent name 
            (field?.toLowerCase().includes(associateName.toLowerCase()) && field?.toLowerCase().includes('name')) //associate name 
        
        const commissionerSalesPerson = isCommissionerField ? getSalesPersonID( field ,val , agentNames) : {};
        const paymentModeVal = field === paymentModeField  ? getPaymentModeID( field ,val , paymentModeOptions) : {};

        //gst type selected; (inclusive / exclusive)
        const gstType = field.includes(gst) ? getGSTType(field, val) : {}; 
        
       

        const detailedOptionDate = field === optionDate ? getDetailedDates_Individuals(field , val) : {}
        const detailedSubmissionDate = (field === submissionDate || field === commencementDateField || field === expiryDateField) ? getDetailedDates_Ordinal(field , val) : {}
        
        //auto fill of tel and cea no.
        const associateTel_CEA = field?.toLowerCase().includes(associateName.toLowerCase()) && field?.toLowerCase().includes('name') ? 
                                    getAssociateTelAndCEA(field, val, agentNames)
                                    :
                                    ""
        //if the associate representative is external agent
        const cobrokeAgentName = 
                field?.toLowerCase().includes(associateName.toLowerCase()) &&
                field?.toLowerCase().includes('1name') //applicable for first associate representative only
                ?
                setCobrokerDetails(field, val, agentNames , mainValues)
                :
                ""
             
        // //cobrokeCommissionDue
        const cobrokeCommissionDue = field === billToField ? getCobrokeCommissionDue(field, val, mainValues , agentNames): {};

        // for rentals , 
        const rentalsCommission = getRentalsCommission(field, val , mainValues); //computation of commission amount and security deposit
        const expiryDate = getExpiryDateForRentals(field, val, mainValues); // auto value of expiry date
        
        const updatedMainValues = {
            ...mainValues,
            ...commissionerSalesPerson,
            ...paymentModeVal,
            ...gstType,
            ...detailedSubmissionDate, ...detailedOptionDate,
            ...associateTel_CEA,
            ...cobrokeAgentName,
            ...rentalsCommission,
            ...expiryDate,
            ...cobrokeCommissionDue,
        };

        //fields with computation
        const computedValues = isPriceOrAmountField ? executeFieldFormula(val, field, updatedMainValues) : {} ;

        dispatch(setFormState({ 
            ...appState.formState, 
            mainValues: {
                ...updatedMainValues, 
                ...computedValues , 
                [field]: val
            } 
        }));

    }, [appState.formState, dispatch, paymentModeOptions]);

    const executeFieldFormula = (val, field , updatedMainValues) => {
        let mainValues = updatedMainValues;
        let allFields = fields;

        // exclusive for commissions only
        const updateFormula = (fieldName, formula) => {
            const formulaField = allFields.find(f => f.FieldName2 === fieldName);
            if (formulaField) {
                formulaField.Formula = formula;
                allFields = allFields.filter(f => f.FieldName2 !== fieldName).concat(formulaField);
            }
        };

        // exclusive for commissions only
        //this will not affect other computations,
        // will run on change of the specified fields
        if (field === cobroke.commissionRate) {
            if(type === defaultType[0]){  //SALES TYPE
                updateFormula(cobroke.commissionAmount, `salesPrice * (${cobroke.commissionRate} / 100)`);
            }
            if(type === defaultType[1]){ //RENTALS TYPE
                updateFormula(cobroke.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${cobroke.commissionRate} / 100)`);
            }
        } 
        else if (field === cobroke.commissionAmount) {
            if(type === defaultType[0]){  //SALES TYPE
                updateFormula(cobroke.commissionRate, `(${cobroke.commissionAmount} / salesPrice) * 100`);
            }
            if(type === defaultType[1]){ //RENTALS TYPE
                updateFormula(cobroke.commissionRate, `(${cobroke.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
            }
        }
        else if (field === agent.commissionRate) {
            updateFormula(agent.commissionAmount, `salesPrice * (${agent.commissionRate} / 100)`);
        } 
        else if (field === agent.commissionAmount) {
            updateFormula(agent.commissionRate, `(${agent.commissionAmount} / salesPrice) * 100`);
        }
        else if (field === buyer.commissionRate) {
            updateFormula(buyer.commissionAmount, `salesPrice * (${buyer.commissionRate} / 100)`);
        } 
        else if (field === buyer.commissionAmount) {
            updateFormula(buyer.commissionRate, `(${buyer.commissionAmount} / salesPrice) * 100`);
        }
        else if (field === tenant.commissionRate) {
            updateFormula(tenant.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${tenant.commissionRate} / 100)`);
        } 
        else if (field === tenant.commissionAmount) {
            updateFormula(tenant.commissionRate, `(${tenant.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
        }
        else if (field === landlord.commissionRate) {
            updateFormula(landlord.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${landlord.commissionRate} / 100)`);
        } 
        else if (field === landlord.commissionAmount) {
            updateFormula(landlord.commissionRate, `(${landlord.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
        }
        else if((field === "salesPrice") || field === "monthlyRental") {
 
            if(type === defaultType[0]){
                updateFormula(cobroke.commissionRate, `(${cobroke.commissionAmount} / salesPrice) * 100`);
                updateFormula(cobroke.commissionAmount, `salesPrice * (${cobroke.commissionRate} / 100)`);
            }
            if(type === defaultType[1]){
                updateFormula(cobroke.commissionRate, `(${cobroke.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
                updateFormula(cobroke.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${cobroke.commissionRate} / 100)`);
            }
            updateFormula(agent.commissionRate, `(${agent.commissionAmount} / salesPrice) * 100`);
            updateFormula(agent.commissionAmount, `salesPrice * (${agent.commissionRate} / 100)`);
            updateFormula(buyer.commissionRate, `(${buyer.commissionAmount} / salesPrice) * 100`);
            updateFormula(buyer.commissionAmount, `salesPrice * (${buyer.commissionRate} / 100)`);
            updateFormula(landlord.commissionRate, `(${landlord.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
            updateFormula(landlord.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${landlord.commissionRate} / 100)`);
            updateFormula(tenant.commissionRate, `(${tenant.commissionAmount} / (((monthlyRental * termsOfLeaseMonths) * 0.5) / 12)) * 100`);
            updateFormula(tenant.commissionAmount, `(((monthlyRental * termsOfLeaseMonths) * 0.5) / 12) * (${tenant.commissionRate} / 100)`);
            // mainValues = {
            //     ...mainValues,
            //     [agent.commissionAmount]: "0",
            //     [agent.commissionRate]: "0",
            //     [cobroke.commissionAmount]: "0",
            //     [cobroke.commissionRate]: "0",
            //     [buyer.commissionAmount]: "0",
            //     [buyer.commissionRate]: "0"
            // }
        }


        // Step 1: Filter fields that have a Formula defined
        const formulaFields = allFields.filter(form => form.Formula);

        // Step 2: Evaluate formulas for each formulaField
        const updatedValues = {};

        // Position the "amountForSales" field before the "nettCommission" field
        // Purpose: To ensure proper calculation
        // Reason: The "amountForSales" field must come before the fields in its formula for accurate real-time computation.
        const amountForSalesField = formulaFields.findIndex(obj => obj.FieldName2 === amountForSales);
        const nettCommissionField = formulaFields.findIndex(obj => obj.FieldName2 === nettCommission);

        let defaultAmountForSalesOrder;
        
        if(amountForSalesField > 0){
            defaultAmountForSalesOrder = formulaFields[amountForSalesField].FieldOrder;
            formulaFields[amountForSalesField].FieldOrder = (parseInt(formulaFields[nettCommissionField].FieldOrder) - 1).toString();
        }

        if (formulaFields.length > 0) {
            try {
                const sortedFormulaFields = sortByFields(formulaFields)
                sortedFormulaFields.forEach(formField => {
                    const { FieldName2, Formula } = formField;

                        const result = evaluateFormula(Formula, { ...mainValues, [field]: val });
                        updatedValues[FieldName2] = Number.isNaN(parseInt(result)) ?  "" : result
                        
                    mainValues = {...mainValues, ...updatedValues , [field]: val}

                    if(FieldName2 === "amountForSales"){
                        formField.FieldOrder = defaultAmountForSalesOrder //reset the field order of the AmountForSales
                    }

                }); 
            } catch (error) {
                console.log("field error:" + field)
                console.log(error)
                return
            }
        }
        
        return updatedValues;
    }

    const getSalesPersonID = ( fieldName ,fieldValue , allAgents) =>{
        const salesPerson = allAgents.find(i=> i.agentName === fieldValue)
        return  { [fieldName + "_ID"]: salesPerson ? salesPerson?.agentID : ""} 
    }
    const getPaymentModeID = ( fieldName ,fieldValue , options) =>{
        const paymentMode = options.find(i=> i.payment_mode?.toLowerCase() === fieldValue?.toLowerCase())
        return  { [fieldName + "_ID"]: paymentMode ? paymentMode?.payment_mode_id : ""} 
    }
    const getGSTType = ( fieldName , fieldValue ) =>{
        const gstTypeField = fieldName.replace("Exclusive" , "Type")
        return  { [gstTypeField]: fieldValue === "Yes" ? "Exclusive" : "Inclusive"} 
    }

    const getAssociateTelAndCEA = ( fieldName ,fieldValue, allAgents ) =>{
        const telNoField = fieldName.replace("Name" , "TelNo")
        const CEANoField = fieldName.replace("Name" , "CEARegNo")
        const agent = allAgents.find(i=> i.agentName === fieldValue)

        return {
            [telNoField]: agent?.agentTelNo ? agent.agentTelNo : "",
            [CEANoField]: agent?.agentCEANo ? agent.agentCEANo : ""
        }
    }

    const getRentalsCommission = ( fieldName, fieldValue , mainValues) => {
        const monthlyRent = mainValues.monthlyRental;
        const terms = mainValues.termsOfLeaseMonths;
        const gstExclusive = mainValues.gSTExclusive
        const gst = mainValues.gST
        
        const gstExclusiveRate = (gst/100)
        const gstInclusiveRate = (1 + (gst / 100))

        let rentConverted = 0;
        let termsConverted = 0;
        let commissionAmountValue = 0;

        const parseValue = (value) => parseFloat(value.replace(/,/g, ''));

        if (fieldName === monthlyRental) {
            rentConverted = parseValue(fieldValue.replace(/,/g, ''))
            termsConverted = parseInt(terms)
            commissionAmountValue = ((rentConverted * termsConverted) * .5) / 12
        } else if (fieldName === termsOfLeaseMonthsField) {
            rentConverted = parseValue(monthlyRent.replace(/,/g, ''))
            termsConverted = parseInt(fieldValue)
            commissionAmountValue = ((rentConverted * termsConverted) * .5) / 12
        } else {
            return {}
        }

        // Calculate commissions
        const securityDeposit = (rentConverted * termsConverted) / 12;
        const commissionAmount2 = (rentConverted * termsConverted * 0.5) / 12;

        const commissionReceivable = gstExclusive === 'No'
            ? (commissionAmountValue / gstInclusiveRate).toString()
            : commissionAmountValue.toString();

        const gSTAmount = gstExclusive === 'No'
            ? (commissionAmountValue - (commissionAmountValue / gstInclusiveRate)).toString()
            : (commissionAmountValue * gstExclusiveRate).toString();

        const totalInvoiceReceivable = (parseValue(commissionReceivable) + parseValue(gSTAmount)).toString();

        // Return the commissions object
        if(rentConverted && termsConverted){ 
            return {
                securityDeposit: addCommasOnNumber(parseFloat(securityDeposit.toString().replace(/,/g, '')).toFixed(2)),
                commissionAmount2: addCommasOnNumber(parseFloat(commissionAmount2.toString().replace(/,/g, '')).toFixed(2)),
                commissionReceivable,
                gSTAmount,
                totalInvoiceReceivable
            };
        }else{
            return {
                securityDeposit: "",
                commissionAmount2: "",
                commissionReceivable: "",
                gSTAmount:"",
                totalInvoiceReceivable: ""
            }
        }
    } 

    const getExpiryDateForRentals = (fieldName, fieldValue, mainValues) => {
        const { termsOfLeaseMonths, commencementDate } = mainValues

        const expiryFormat = (terms, commencement) => dayjs(commencement).add(terms, 'month').subtract(1, 'day').format('DD-MMM-YYYY');
        
        if (fieldName === commencementDateField && termsOfLeaseMonths) {
            return {
                expiryDate: expiryFormat(termsOfLeaseMonths , fieldValue),
                ...getDetailedDates_Ordinal('expiryDate' , expiryFormat(termsOfLeaseMonths , fieldValue))
            }

        } else if (fieldName === termsOfLeaseMonthsField && commencementDate) {
            if(fieldValue && parseInt(fieldValue) > 0){
                return {
                    expiryDate:  expiryFormat(fieldValue , commencementDate),
                    ...getDetailedDates_Ordinal('expiryDate' , expiryFormat(fieldValue , commencementDate))
                }
            }
        } else {
            return {}
        }

    }
    
    const setCobrokerDetails = ( fieldName ,fieldValue, allAgents, mainValues ) => {
        const { cobrokeCommissionDue } = mainValues
        const _seller = fieldName?.toLowerCase().includes('seller')
        const _landlord = fieldName?.toLowerCase().includes('landlord')
        const _buyer = fieldName?.toLowerCase().includes('buyer')
        const _tenant = fieldName?.toLowerCase().includes('tenant')


        const agent = allAgents.find(i=> i.agentName === fieldValue)
        if(cobrokeCommissionDue === "To" && (_buyer || _tenant)){
            return {
                cobrokeAgentName: agent ? "" : fieldValue || "",
            }
        }
        if(cobrokeCommissionDue === "From" && (_seller || _landlord)){
            return {
                cobrokeAgentName: agent ? "" : fieldValue || "",
            }
        }
    }

    const getCobrokeCommissionDue = (fieldName, fieldValue, mainValues , allAgents) => {
        const _cobroke = fieldValue.toLowerCase().includes("co-broke");
        const _seller = fieldName?.toLowerCase().includes('seller')
        const _landlord = fieldName?.toLowerCase().includes('landlord')
        const _buyer = fieldName?.toLowerCase().includes('buyer')
        const _tenant = fieldName?.toLowerCase().includes('tenant')
        const assocRepSeller = mainValues.sellerAssociate1Name_ID ? "" : mainValues.sellerAssociate1Name || ""
        const assocRepBuyer = mainValues.buyerAssociate1Name_ID ? "" : mainValues.buyerAssociate1Name || ""
        const assocRepLandlord = mainValues.landlordAssociate1Name_ID ? "" : mainValues.landlordAssociate1Name || ""
        const assocRepTenant = mainValues.tenantAssociate1Name_ID ? "" : mainValues.tenantAssociate1Name || ""
        
        return {
            cobrokeCommissionDue: _cobroke ? "From" : "To",
            buyerCommissionFrom: _cobroke ? "" : ((_buyer || _tenant) ? fieldValue : ""),
            sellerCommissionFrom: _cobroke ? "" : ((_seller || _landlord) ? fieldValue : ""),
            cobrokeAgentName: _cobroke ? (_seller ? assocRepSeller : assocRepLandlord)  : (_buyer ? assocRepBuyer : assocRepTenant),
        }

    }

    const handleFormFileUpload = (file , fieldName) => {
        const validatedFile = validateFile(file);
        if(validatedFile.valid){
            dispatch(setFormState({
                ...appState.formState,
                mainValues: { ...appState.formState.mainValues, [fieldName]: file}
            }));
        }else{
            dispatch(setAlert({
                show: true,
                title: 'ERROR: UPLOAD',
                body: `${validatedFile.errMsg}`,
            }))
        }
    }
    
    return {
        mainFields,
        mainSections,
        mainFormState,
        mainFormErrors,
        handleMainFieldChange,
        isMounted,
        pageError,
        handleFormFileUpload
    }
}

export default useMainForm;